"use client";

import { useActionState } from "react";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { MinusIcon } from "@/components/icons/minus.icon";
import { PlusIcon } from "@/components/icons/plus.icon";
import { TrashIcon } from "@/components/icons/trash.icon";
import { Loader } from "@/components/loader/loader.component";
import { Text } from "@/components/text/text.component";

import { QuantityStepperAdd } from "./quantityStepperAdd.component";

const initialState: ResponseMessage = { success: false };

export const QuantityStepper = ({
  id,
  quantity = 0,
  formAction,
  onRemove,
  onInteraction,
}: QuantityStepperProps) => {
  const [, action, pending] = useActionState<ResponseMessage, FormData>(
    formAction,
    initialState,
  );
  const isLast = quantity === 1;

  const handleAction = async (formData: FormData) => {
    const kitId = document
      .querySelector('input[name="kitId"]')
      ?.getAttribute("value");
    const cameraId = document
      .querySelector('input[name="cameraId"]')
      ?.getAttribute("value");
    const monitoringPlanId = document
      .querySelector('input[name="monitoringPlan"]')
      ?.getAttribute("value");
    const productRoot = document.querySelector(
      `[data-product-card-id="${id}"]`,
    );
    const variantId = productRoot
      ?.querySelector('input[name="variantId"]')
      ?.getAttribute("value");
    const option = document
      .querySelector('input[name="option"]:checked')
      ?.getAttribute("value");
    if (kitId) formData.set("kitId", kitId);
    if (cameraId) formData.set("cameraId", cameraId);
    if (monitoringPlanId) formData.set("monitoringPlan", monitoringPlanId);
    if (variantId) formData.set("variantId", variantId);
    formData.set("productId", id);
    if (option) formData.set("option", option);

    const actionType = formData.get("action") as string;
    onInteraction?.(actionType === "increase" ? "add" : "remove");

    action(formData);
  };
  if (!quantity) {
    return (
      <QuantityStepperAdd
        id={id}
        handleAction={handleAction}
        pending={pending}
      />
    );
  }
  return (
    <div
      className="border-secondary mt-1 flex w-fit items-center rounded-full border p-1"
      aria-busy={pending}>
      <form action={handleAction} className="inline-flex" aria-busy={pending}>
        <input type="hidden" name="action" value="decrease" />
        <input type="hidden" name="itemId" value={id} />
        <Button
          type="submit"
          onClick={isLast ? onRemove : undefined}
          className={cn(
            `flex h-8 w-8 cursor-pointer items-center justify-center bg-transparent p-0 text-neutral-800 hover:bg-transparent`,
            {
              "pointer-events-none": pending,
            },
          )}
          aria-disabled={pending}
          disabled={pending}
          aria-label={isLast ? "Remove item" : "Decrease quantity"}>
          {isLast ? (
            <TrashIcon
              width={16}
              height={16}
              className="text-greyscale-strength"
            />
          ) : (
            <MinusIcon width={18} height={18} />
          )}
        </Button>
      </form>
      <div className="flex min-w-8 flex-1 justify-center">
        {pending ? (
          <Loader loading size="sm" />
        ) : (
          <Text
            variant="body2"
            component="span"
            className="bold text-secondary flex items-center justify-center px-2">
            {quantity}
          </Text>
        )}
      </div>
      <form action={handleAction} className="inline-flex" aria-busy={pending}>
        <input type="hidden" name="action" value="increase" />
        <input type="hidden" name="itemId" value={id} />
        <Button
          type="submit"
          className={cn(
            `flex h-8 w-8 cursor-pointer items-center justify-center bg-transparent p-0 text-neutral-800 hover:bg-transparent`,
            {
              "pointer-events-none": pending,
            },
          )}
          aria-disabled={pending}
          aria-label="Increase quantity"
          disabled={pending}>
          <PlusIcon width={20} height={20} />
        </Button>
      </form>
    </div>
  );
};

type QuantityStepperProps = {
  id: string;
  quantity?: number;
  formAction: (
    prevState: ResponseMessage,
    formData: FormData,
  ) => ResponseMessage | Promise<ResponseMessage>;
  onRemove?: () => void;
  onInteraction?: (action: "add" | "remove") => void;
};
